import React from 'react';
import OrgMark, { useOrgDocumentIdentity } from '@/components/OrgMark';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { BarChart2, CalendarDays, ChevronLeft, ChevronRight, FileSignature, GraduationCap, ListChecks, LogOut, Menu, MessageSquare, Moon, Route, ShieldAlert, Sun, Sunrise, Sparkles, Shirt, User, UserCircle, CalendarRange, Home, Truck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { useTheme } from '@/contexts/ThemeContext';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabaseClient';
import BottomNavBar from '@/components/subcontractor/BottomNavBar';
import FieldAssistFab from '@/components/subcontractor/FieldAssistFab';
import ProtocolAlarmManager from '@/components/subcontractor/ProtocolAlarmManager';
import PushTokenRefresher from '@/components/subcontractor/PushTokenRefresher';
import PushForegroundListener from '@/components/subcontractor/PushForegroundListener';
import { FIELD_ASSIST_ENABLED, UNIFORM_ORDERING_LIVE } from '@/lib/featureFlags';
import useModuleEnabled, { useActiveModuleKeys } from '@/hooks/useModuleEnabled';
import useOrg from '@/contexts/OrgContext';
import { FLAGSHIP_ORG } from '@/lib/moduleAccess';
import { partAccess, CREW_PATH_PART } from '@/lib/partSurfaces';
import { useDemoAccess } from '@/contexts/DemoAccessContext';
import { partForRoute, PART_META } from '@/lib/demoScenes';
import { Lock, Archive } from 'lucide-react';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';

const sidebarNavItems = [
  { to: '/subcontractor/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/subcontractor/dashboard/availability', icon: CalendarRange, label: 'Availability' },
  { to: '/subcontractor/dashboard/logs', icon: CalendarDays, label: 'Route Logs' },
  { to: '/subcontractor/dashboard/performance', icon: BarChart2, label: 'Performance' },
  { to: '/subcontractor/dashboard/live-invoice', icon: FileSignature, label: 'Live Invoice' },
  { to: '/subcontractor/dashboard/messages', icon: MessageSquare, label: 'Messages' },
  { to: '/subcontractor/dashboard/cases', icon: ShieldAlert, label: 'Cases' },
  { to: '/subcontractor/dashboard/my-day', icon: Sunrise, label: 'My Day' },
  // My Coach is a part (module key 'my-coach'): flagship crews see it by
  // default; a tenant's crews see it when the part is on their plan.
  { to: '/subcontractor/dashboard/my-coach', icon: Sparkles, label: 'My Coach' },
  // Coming soon until the operations side is finalised — the link shows but
  // can't be selected (UNIFORM_ORDERING_LIVE in featureFlags.js flips it on).
  { to: '/subcontractor/dashboard/uniform', icon: Shirt, label: 'Uniform', comingSoon: !UNIFORM_ORDERING_LIVE },
  // External: the training platform lives off-portal, so this opens a new tab
  // rather than routing in the SPA.
  { to: 'https://logisticshub.learn.link', icon: GraduationCap, label: 'Training', external: true },
  { to: '/subcontractor/dashboard/profile', icon: User, label: 'Profile' },
];

const Layout = ({ children, session, isDashboard }) => {
  useOrgDocumentIdentity();
  // The crew app is the surface a subcontractor lives in, so it carries their
  // operator's brand — logo where one was uploaded, the company's name in text
  // where it was not. Never the platform's mark for somebody else's business.
  const location = useLocation();
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useUnreadMessages();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Every entry belongs to a part (src/lib/partSurfaces.js), and a crew sees
  // the parts their operator owns: Route Logs is Routes → Pay, Live Invoice is
  // the Invoicing Engine, My Day is My Day. The flagship fails safe to ON; a
  // tenant's crews see nothing their operator has not bought, and the moment
  // the operator adds a part it appears here without a reload.
  const { orgKey: navOrgKey, isFlagship, loaded: orgLoaded } = useOrg();
  const parts = useActiveModuleKeys();
  const partAccessState = { keys: parts.keys, loaded: parts.loaded && orgLoaded, isFlagship: orgLoaded && isFlagship };
  // My Coach ships flagship-first: the entitlement projection only lists keys
  // registered in platform_parts, and registering the 'my-coach' part belongs
  // to the control-plane session — until that row exists, the flagship org
  // keeps the page (its crews are the launch cohort) and other tenants wait
  // for the part.
  const { enabled: myCoachModuleOn } = useModuleEnabled('my-coach');
  const myCoachEnabled = myCoachModuleOn || (navOrgKey ?? FLAGSHIP_ORG) === FLAGSHIP_ORG;
  // Archive mode (contract terminated): every feature renders locked and the
  // one live page — Your archive — heads the list. ArchiveGate enforces the
  // same truth for typed URLs; this is just the honest picture of it.
  const { archiveLocked } = useSubcontractorProfile();
  const filteredNavItems = sidebarNavItems.filter((item) => {
    if (item.to === '/subcontractor/dashboard/my-coach') return myCoachEnabled;
    return partAccess(item.to, partAccessState, CREW_PATH_PART) === 'on';
  });
  const navItems = archiveLocked
    ? [{ to: '/subcontractor/dashboard/archive', icon: Archive, label: 'Your archive' }, ...filteredNavItems]
    : filteredNavItems;

  // Demo scene lock: on a demo device mid-presentation, parts not yet
  // presented render frosted with their part chip (the locked menu doubles as
  // a pricing page). Inert for real crews — the hook says everything's open.
  const demoAccess = useDemoAccess();
  const demoLockedItem = (to) => demoAccess.active && !demoAccess.isRouteOpen(to);

  const handleLogout = async () => {
    // Local-scope sign-out never fails on a missing session (installed PWA),
    // so logout always works and lands on the login screen.
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (_e) {
      /* already signed out */
    }
    try { window.localStorage.removeItem('parcelink_session_started_at'); } catch (_e) {}
    toast({ title: 'Logged out', description: 'You have been successfully logged out.' });
    window.location.replace('/subcontractor/login');
  };

  const navLinkClasses = (to) => cn(
    'flex min-h-11 items-center rounded-2xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
    location.pathname === to || location.pathname.startsWith(`${to}/`)
      ? 'bg-primary text-white shadow-glow'
      : 'text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground'
  );

  if (!isDashboard) {
    return (
      <div className="shell-gradient min-h-screen text-foreground">
        <header className="sticky top-0 z-40 border-b border-border/70 bg-background/70 backdrop-blur-xl">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
            <Link to="/" className="flex items-center shrink-0">
              <OrgMark logoClassName="h-10 w-auto object-contain" textClassName="text-base font-extrabold tracking-tight" />
            </Link>
            
            <div className="flex flex-grow items-center justify-end gap-3">
              {session && (
                <span className="hidden sm:inline-flex items-center text-[10px] font-black uppercase tracking-widest text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full border border-border/50">
                  <UserCircle size={14} className="mr-2 text-primary" />
                  {session.user?.email}
                </span>
              )}
              {session && (
                <Button onClick={handleLogout} variant="outline" className="h-9 px-4 rounded-xl border-border/50 font-black uppercase text-[10px] tracking-widest">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </Button>
              )}
            </div>
          </div>
        </header>
        <main className="mx-auto flex w-full max-w-7xl flex-col px-4 py-6 sm:px-6 sm:py-8 lg:px-8">{children}</main>
      </div>
    );
  }

  const Sidebar = ({ mobile = false }) => (
    <div className="sidebar-panel flex h-full flex-col border-r border-sidebar-border">
      <div className="flex items-center justify-between border-b border-sidebar-border px-4 py-4">
        {(isSidebarOpen || mobile)
          ? <OrgMark logoClassName="h-8 w-auto object-contain" textClassName="text-sm font-extrabold tracking-tight" />
          : <div className="h-8 w-8 rounded-2xl bg-primary/40" />}
        <div className="flex items-center gap-2">
          {!mobile && <Button variant="ghost" size="icon" className="text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground" onClick={() => setIsSidebarOpen((prev) => !prev)}>{isSidebarOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}</Button>}
          {mobile && <Button variant="ghost" size="icon" className="text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground" onClick={() => setIsMobileMenuOpen(false)}><ChevronLeft className="h-5 w-5" /></Button>}
        </div>
      </div>

      <nav className="premium-scrollbar flex-1 space-y-1.5 overflow-y-auto p-3">
        {navItems.map((item) => {
          const Icon = item.icon;
          if (item.external) {
            return (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => mobile && setIsMobileMenuOpen(false)}
                className={cn(navLinkClasses(item.to), !isSidebarOpen && !mobile && 'justify-center px-0')}
              >
                <Icon className={cn('h-5 w-5 shrink-0', (isSidebarOpen || mobile) && 'mr-3')} />
                {(isSidebarOpen || mobile) && <span className="truncate">{item.label}</span>}
              </a>
            );
          }
          // Archive mode: everything except the archive itself is locked —
          // visible, so the person can see what their portal held, but shut.
          if (archiveLocked && item.to !== '/subcontractor/dashboard/archive') {
            return (
              <div
                key={item.to}
                aria-disabled="true"
                title="Locked — your contract has ended. Your archive holds your records."
                className={cn(
                  'flex min-h-11 cursor-not-allowed select-none items-center rounded-2xl px-3 py-2.5 text-sm font-medium text-sidebar-foreground/35',
                  !isSidebarOpen && !mobile && 'justify-center px-0',
                )}
              >
                <Icon className={cn('h-5 w-5 shrink-0 opacity-50', (isSidebarOpen || mobile) && 'mr-3')} />
                {(isSidebarOpen || mobile) && <span className="truncate opacity-60">{item.label}</span>}
                {(isSidebarOpen || mobile) && <Lock className="ml-auto h-3.5 w-3.5 opacity-70" />}
              </div>
            );
          }
          // Coming soon: visible so crews know it's on the way, but not
          // selectable until the operations side is signed off.
          if (item.comingSoon) {
            return (
              <div
                key={item.to}
                aria-disabled="true"
                title={`${item.label} is coming soon`}
                className={cn(
                  'flex min-h-11 cursor-not-allowed select-none items-center rounded-2xl px-3 py-2.5 text-sm font-medium text-sidebar-foreground/35',
                  !isSidebarOpen && !mobile && 'justify-center px-0',
                )}
              >
                <Icon className={cn('h-5 w-5 shrink-0 opacity-50', (isSidebarOpen || mobile) && 'mr-3')} />
                {(isSidebarOpen || mobile) && <span className="truncate opacity-60">{item.label}</span>}
                {(isSidebarOpen || mobile) && (
                  <span className="ml-auto rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                    Coming soon
                  </span>
                )}
              </div>
            );
          }
          // Demo scene lock: a part the presentation hasn't opened yet renders
          // frosted with a lock and its part chip — visibly a locked product,
          // never a broken link. No navigation until the presenter opens it.
          if (demoLockedItem(item.to)) {
            const part = PART_META[partForRoute(item.to)];
            return (
              <div
                key={item.to}
                aria-disabled="true"
                title={part ? `${part.name} unlocks later in the presentation` : 'Unlocks later in the presentation'}
                className={cn(
                  'flex min-h-11 cursor-not-allowed select-none items-center rounded-2xl px-3 py-2.5 text-sm font-medium text-sidebar-foreground/35',
                  !isSidebarOpen && !mobile && 'justify-center px-0',
                )}
              >
                <Icon className={cn('h-5 w-5 shrink-0 opacity-50', (isSidebarOpen || mobile) && 'mr-3')} />
                {(isSidebarOpen || mobile) && <span className="truncate opacity-60">{item.label}</span>}
                {(isSidebarOpen || mobile) && (
                  <span className="ml-auto flex items-center gap-1.5">
                    {part && demoAccess.showPricing && (
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-bold text-amber-600 dark:text-amber-400">
                        £{part.price}/crew
                      </span>
                    )}
                    <Lock className="h-3.5 w-3.5 opacity-70" />
                  </span>
                )}
              </div>
            );
          }
          return (
            <NavLink key={item.to} to={item.to} onClick={() => mobile && setIsMobileMenuOpen(false)} className={cn(navLinkClasses(item.to), !isSidebarOpen && !mobile && 'justify-center px-0')}>
              <Icon className={cn('h-5 w-5 shrink-0', (isSidebarOpen || mobile) && 'mr-3')} />
              {(isSidebarOpen || mobile) && <span className="truncate">{item.label}</span>}
              {item.label === 'Messages' && unreadCount > 0 && (isSidebarOpen || mobile) && (
                <span className="ml-auto flex items-center gap-1.5">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-indigo-600 shadow-[0_0_10px_rgba(79,70,229,0.5)]" />
                  <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                </span>
              )}
              {item.label === 'Messages' && unreadCount > 0 && !(isSidebarOpen || mobile) && (
                <span className="absolute right-3 top-2 flex">
                  <span className="absolute inline-flex h-2.5 w-2.5 animate-ping rounded-full bg-indigo-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-indigo-600" />
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3">
        <button
          onClick={toggleTheme}
          className={cn(
            "w-full flex items-center p-3 rounded-xl transition-all duration-300",
            "bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800",
            "hover:bg-slate-100 dark:hover:bg-slate-800 group shadow-sm",
            !isSidebarOpen && !mobile && 'justify-center'
          )}
        >
          {theme === 'dark' ? (
            <>
              <Sun className="h-5 w-5 text-yellow-500 shrink-0" />
              {(isSidebarOpen || mobile) && <span className="ml-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Light Mode</span>}
            </>
          ) : (
            <>
              <Moon className="h-5 w-5 text-indigo-500 shrink-0" />
              {(isSidebarOpen || mobile) && <span className="ml-3 text-sm font-semibold text-slate-700 dark:text-slate-300">Dark Mode</span>}
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="shell-gradient flex min-h-screen bg-background text-foreground">
      <aside className={cn('sticky top-0 hidden h-screen shrink-0 lg:block', isSidebarOpen ? 'w-[260px]' : 'w-[92px]')}>
        <Sidebar />
      </aside>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-slate-950/55 backdrop-blur-sm lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
            <motion.aside initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} transition={{ type: 'spring', stiffness: 320, damping: 28 }} className="fixed inset-y-0 left-0 z-50 w-[50vw] max-w-[280px] lg:hidden">
              <Sidebar mobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="sticky top-0 z-30 border-b border-border/70 bg-background/70 backdrop-blur-xl lg:hidden"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 relative">
            <div className="flex items-center gap-3 overflow-hidden shrink-0">
              <OrgMark className="shrink-0" logoClassName="h-8 w-auto object-contain" textClassName="text-sm font-extrabold tracking-tight" />
            </div>

            <div className="absolute left-1/2 -translate-x-1/2 flex justify-center px-2 overflow-hidden w-auto max-w-[40%]">
               <span className="text-[14px] font-black uppercase tracking-tight text-slate-900 dark:text-white truncate">
                 {navItems.find(item => location.pathname === item.to || (item.to !== '/subcontractor/dashboard' && location.pathname.startsWith(item.to)))?.label || 'Portal'}
               </span>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {FIELD_ASSIST_ENABLED && demoAccess.isPartOpen('field-assist') && (
                <Link to="/subcontractor/dashboard/field-assist" aria-label="Field Assist">
                  <Button variant="ghost" size="icon" className="rounded-xl h-9 w-9">
                    <img src="/field-assist-logo.png" alt="" className="h-6 w-6" />
                  </Button>
                </Link>
              )}
              {demoAccess.isPartOpen('operations') && (
                <Link to="/subcontractor/dashboard/availability">
                  <Button variant="ghost" size="icon" className="rounded-xl h-9 w-9 text-slate-600 dark:text-slate-400">
                    <CalendarRange className="h-5 w-5" />
                  </Button>
                </Link>
              )}
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="rounded-xl h-9 w-9">
                <Menu className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </header>

        <main className="page-wrap flex-1 pb-24 lg:pb-8">{children}</main>
      </div>

      <BottomNavBar />
      {FIELD_ASSIST_ENABLED && demoAccess.isPartOpen('field-assist')
        && partAccess('/subcontractor/dashboard/field-assist', partAccessState, CREW_PATH_PART) === 'on'
        && <FieldAssistFab />}
      {/* Full-screen in-app alarms for activated protocol steps (subcontractor-wide). */}
      <ProtocolAlarmManager />
      {/* Silent fcm_token refresh on app open — tokens rot; first-grant-only persistence left the roster stale. */}
      <PushTokenRefresher />
      {/* A push that arrives while the portal is open goes to onMessage, which
          the service worker never sees. Without this it was dropped in silence. */}
      <PushForegroundListener />
    </div>
  );
};

export default Layout;